var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useRef, useState } from 'react';
import { Accordion, Button, InlineNotification, Text } from '@ui';
import { CHECKLIST_ITEM_KEY, CHECKLIST_STATUS } from '../shared/constants';
import { translatorFor } from '../shared/i18n';
import { humanize } from '../shared/types';
import { EquipmentMatch } from './EquipmentMatch';
import { MissingEquipmentTypes } from './MissingEquipmentTypes';
const t = translatorFor('setup_results');
const PASSING_STATUSES = [CHECKLIST_STATUS.OK, CHECKLIST_STATUS.CREATED];
function TechnicianProblems({ data }) {
    return (React.createElement("ul", { className: "service-titan-settings__plain-list" },
        data.unmatched > 0 && React.createElement("li", null, t('technicians_unmatched', { count: data.unmatched })),
        data.ambiguous > 0 && React.createElement("li", null, t('technicians_ambiguous', { count: data.ambiguous })),
        data.updateFailed > 0 && React.createElement("li", null, t('technicians_update_failed', { count: data.updateFailed })),
        data.total === 0 && React.createElement("li", null, t('technicians_none_found'))));
}
export function SetupResults({ verification, urls, equipmentTypeMap, deviceTypeOptions, pulling, pullDisabled, onRecheck, onEquipmentTypeMapChange, onEquipmentTypeIgnored, onPull, onExpandedChange }) {
    var _a, _b, _c, _d;
    const [expanded, setExpanded] = useState(true);
    const [rechecking, setRechecking] = useState(false);
    const recheckInFlight = useRef(false);
    const [recheckError, setRecheckError] = useState(null);
    const items = (_b = (_a = verification.checklist) === null || _a === void 0 ? void 0 : _a.items) !== null && _b !== void 0 ? _b : [];
    const passed = items.filter((item) => PASSING_STATUSES.includes(item.status));
    const problems = items.filter((item) => !PASSING_STATUSES.includes(item.status));
    const passingEquipmentTypes = (_c = passed.find((item) => item.key === CHECKLIST_ITEM_KEY.EQUIPMENT_TYPES && item.equipmentTypes)) === null || _c === void 0 ? void 0 : _c.equipmentTypes;
    const mappedTypes = new Set(equipmentTypeMap.map((row) => row.ipSystemDeviceType));
    const unmappedTypes = deviceTypeOptions.filter((kind) => !mappedTypes.has(kind));
    const problemCount = problems.length + (unmappedTypes.length > 0 ? 1 : 0);
    const handleToggle = (next) => {
        setExpanded(next);
        onExpandedChange === null || onExpandedChange === void 0 ? void 0 : onExpandedChange(next);
    };
    const recheck = () => __awaiter(this, void 0, void 0, function* () {
        if (recheckInFlight.current)
            return;
        recheckInFlight.current = true;
        setRechecking(true);
        setRecheckError(null);
        try {
            yield onRecheck();
        }
        catch (_a) {
            setRecheckError(t('recheck_failed'));
        }
        finally {
            recheckInFlight.current = false;
            setRechecking(false);
        }
    });
    const renderProblemBody = (item) => {
        var _a;
        if (item.key === CHECKLIST_ITEM_KEY.EQUIPMENT_TYPES && item.equipmentTypes) {
            return (React.createElement(EquipmentMatch, { data: item.equipmentTypes, urls: urls, equipmentTypeMap: equipmentTypeMap, deviceTypeOptions: deviceTypeOptions, onEquipmentTypeMapChange: onEquipmentTypeMapChange, onEquipmentTypeIgnored: onEquipmentTypeIgnored }));
        }
        if (item.key === CHECKLIST_ITEM_KEY.TECHNICIANS && item.technicians) {
            return React.createElement(TechnicianProblems, { data: item.technicians });
        }
        if (item.key.startsWith(CHECKLIST_ITEM_KEY.JOB_CUSTOM_FIELD_PREFIX)) {
            return (React.createElement(Text, { variant: "body", as: "p" }, t('create_field', { name: item.label })));
        }
        return (React.createElement(Text, { variant: "body", as: "p" }, (_a = item.actionText) !== null && _a !== void 0 ? _a : item.detail));
    };
    return (React.createElement(Accordion, { variant: "section", title: t('title'), isExpanded: expanded, onToggle: handleToggle },
        React.createElement("div", { className: "service-titan-settings__accordion-body" },
            verification.unavailable && (React.createElement(InlineNotification, { variant: "warning" }, (_d = verification.message) !== null && _d !== void 0 ? _d : t('unavailable'))),
            !verification.unavailable && (React.createElement(InlineNotification, { variant: "success" }, problemCount === 0 ? t('setup_complete') : t('setup_complete_with_problems', { count: passed.length }))),
            problems.map((item) => (React.createElement("div", { key: item.key, className: "service-titan-settings__section" },
                React.createElement(Text, { variant: "small", as: "h3" }, item.label),
                renderProblemBody(item)))),
            passingEquipmentTypes && (React.createElement(MissingEquipmentTypes, { catalog: passingEquipmentTypes.catalog, urls: urls, onEquipmentTypeMapChange: onEquipmentTypeMapChange, onCreated: () => {
                    void recheck();
                } })),
            unmappedTypes.length > 0 && (React.createElement("div", { className: "service-titan-settings__section" },
                React.createElement(InlineNotification, { variant: "warning" }, `${t('unmapped_warning', { types: unmappedTypes.map(humanize).join(', ') })} ${t('unmapped_hint')}`))),
            recheckError && React.createElement(InlineNotification, { variant: "error" }, recheckError),
            React.createElement("div", { className: "service-titan-settings__actions" },
                (problems.length > 0 || verification.unavailable) && (React.createElement(Button, { variant: "secondary", disabled: rechecking, onClick: () => {
                        void recheck();
                    } }, rechecking ? t('rechecking') : t('recheck'))),
                React.createElement(Button, { variant: "primary", disabled: pulling || pullDisabled, onClick: onPull }, pulling ? t('pulling') : t('pull_buildings'))))));
}
export default SetupResults;
